import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { NewsletterService, type NewsletterSubscribeData } from '~/services/NewsletterService';
import { useNuxtApp } from '#app';

interface NewsletterResponse {
  status: string;
  message: string;
}

/**
 * Bülten aboneliği işlemlerini yönetmek için composable
 */
export function useNewsletter() {
  // Toast bildirimleri için
  const toast = useToast();
  // reCAPTCHA için 
  const nuxtApp = useNuxtApp();
  
  // Newsletter servisini oluştur
  const newsletterService = NewsletterService.getInstance();
  
  // State değişkenleri
  const email = ref<string>('');
  const isLoading = ref<boolean>(false);
  const isSuccess = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  // Email doğrulama fonksiyonu
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Abone olma işlemi
  const subscribe = async (): Promise<boolean> => {
    // Gerekli kontroller
    if (!email.value) {
      error.value = 'E-posta adresi zorunludur.';
      toast.error(error.value);
      return false;
    }
    
    if (!isValidEmail(email.value)) {
      error.value = 'Geçerli bir e-posta adresi giriniz.';
      toast.error(error.value);
      return false;
    }
    
    // İşlem başlatıldı
    isLoading.value = true;
    error.value = null;
    isSuccess.value = false;
    
    try {
      // reCAPTCHA token'ı al
      let recaptchaToken = '';
      try {
        // Plugin'den sağlanan recaptcha kullan
        if (nuxtApp.$recaptcha) {
          recaptchaToken = await nuxtApp.$recaptcha.execute('newsletter_form');
        } else {
          console.warn('reCAPTCHA kullanılabilir değil, token üretilmedi');
        }
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError);
        error.value = 'Güvenlik doğrulaması başarısız oldu. Lütfen daha sonra tekrar deneyiniz.';
        toast.error(error.value);
        isLoading.value = false;
        return false;
      }
      
      // API isteği gönder
      const response: NewsletterResponse = await newsletterService.subscribe(email.value, recaptchaToken);
      
      // Başarılı yanıt (200 veya 201 ise başarılı)
      if (response.status === '200' || response.status === '201' || response.status === 'success') {
        isSuccess.value = true;
        toast.success('Bülten aboneliğiniz başarıyla gerçekleşti!');
        // Formu temizle
        email.value = '';
        return true;
      } else {
        error.value = response.message || 'Bülten aboneliği sırasında bir hata oluştu.';
        toast.error(error.value);
        return false;
      }
    } catch (err) {
      // Hata durumu
      error.value = 'Bülten aboneliği sırasında bir hata oluştu.';
      toast.error(error.value);
      console.error('Newsletter abonelik hatası:', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };
  
  return {
    email,
    isLoading,
    isSuccess,
    error,
    subscribe
  };
} 